import type { Dialect, Diagnostic } from "../shared/contracts.js";
export interface Token {
  kind: "word" | "identifier" | "string" | "number" | "symbol" | "directive";
  text: string;
  value: string;
  start: number;
  end: number;
  line: number;
  quoted?: boolean;
}
export interface Statement {
  tokens: Token[];
  text: string;
  line: number;
  start: number;
  end: number;
}
export const MAX_SOURCE = 50 * 1024 * 1024;
export function tokenize(
  sql: string,
  dialect: Dialect,
): { tokens: Token[]; diagnostics: Diagnostic[] } {
  const tokens: Token[] = [],
    diagnostics: Diagnostic[] = [];
  let i = 0,
    line = 1;
  const issue = (code: string, message: string, at = line) =>
    diagnostics.push({
      code,
      severity: "error",
      message,
      line: at,
      helpTopic: "ddl-import",
    });
  if (sql.includes("\0") || sql.includes("\uFFFD")) {
    issue("DDL_TEXT_ENCODING", "SQL contains NUL or replacement characters. Decode the original bytes with the correct encoding before review.");
    return { tokens, diagnostics };
  }
  if (sql.length > MAX_SOURCE) {
    issue("DDL_SIZE_LIMIT", "SQL exceeds the 50 MiB character budget.");
    return { tokens, diagnostics };
  }
  const push = (
    kind: Token["kind"],
    start: number,
    at: number,
    value?: string,
    quoted = false,
  ) =>
    tokens.push({
      kind,
      text: sql.slice(start, i),
      value: value ?? sql.slice(start, i),
      start,
      end: i,
      line: at,
      ...(quoted ? { quoted: true } : {}),
    });
  while (i < sql.length) {
    if (tokens.length >= 1000000) {
      issue("DDL_TOKEN_LIMIT", "Token count exceeds 1,000,000.");
      break;
    }
    const c = sql[i],
      start = i,
      at = line;
    if (/\s/.test(c)) {
      if (c === "\n") line++;
      i++;
      continue;
    }
    if (sql.startsWith("--", i)) {
      while (i < sql.length && sql[i] !== "\n") i++;
      continue;
    }
    if (sql.startsWith("/*", i)) {
      i += 2;
      let depth = 1;
      while (i < sql.length && depth) {
        if (sql.startsWith("/*", i)) {
          depth++;
          i += 2;
          if (depth > 64) {
            issue("DDL_NESTING_LIMIT", "Comment nesting exceeds 64.", at);
            return { tokens, diagnostics };
          }
        } else if (sql.startsWith("*/", i)) {
          depth--;
          i += 2;
        } else {
          if (sql[i] === "\n") line++;
          i++;
        }
      }
      if (depth)
        issue("DDL_UNTERMINATED_COMMENT", "Unterminated block comment.", at);
      if (dialect === "mysql" && sql.startsWith("/*!", start))
        push("directive", start, at);
      continue;
    }
    if (dialect === "mysql" && c === "#") {
      while (i < sql.length && sql[i] !== "\n") i++;
      continue;
    }
    if (
      /^[ \t]*$/.test(sql.slice(sql.lastIndexOf("\n", i - 1) + 1, i)) &&
      ((dialect === "mysql" && /^delimiter\b/i.test(sql.slice(i))) ||
       (dialect === "postgresql" && c === "\\") ||
       (dialect === "oracle" && /^(?:SET\s+(?:DEFINE|ECHO|FEEDBACK|HEADING|PAGESIZE|LINESIZE|SERVEROUTPUT|SQLBLANKLINES)|PROMPT|SPOOL|WHENEVER|REM(?:ARK)?|EXIT|CONNECT)\b/i.test(sql.slice(i))))
    ) {
      while (i < sql.length && sql[i] !== "\n") i++;
      push("directive", start, at);
      continue;
    }
    const dollar =
      dialect === "postgresql" && c === "$"
        ? sql.slice(i).match(/^\$(?:[a-zA-Z_][a-zA-Z0-9_]*)?\$/)?.[0]
        : undefined;
    if (dollar) {
      i += dollar.length;
      const end = sql.indexOf(dollar, i);
      if (end < 0) {
        issue(
          "DDL_UNTERMINATED_LITERAL",
          "Unterminated dollar-quoted body.",
          at,
        );
        i = sql.length;
      } else i = end + dollar.length;
      line += (sql.slice(start, i).match(/\n/g) || []).length;
      push("string", start, at);
      continue;
    }
    if (dialect === "oracle" && /^[qQ]'./.test(sql.slice(i))) {
      const open = sql[i + 2],
        close: Record<string, string> = {
          "[": "]",
          "{": "}",
          "(": ")",
          "<": ">",
        };
      i += 3;
      const end = sql.indexOf((close[open] ?? open) + "'", i);
      if (end < 0) {
        issue(
          "DDL_UNTERMINATED_LITERAL",
          "Unterminated Oracle quoted literal.",
          at,
        );
        i = sql.length;
      } else i = end + 2;
      line += (sql.slice(start, i).match(/\n/g) || []).length;
      push("string", start, at);
      continue;
    }
    if (
      c === "'" ||
      c === '"' ||
      c === "`" ||
      (c === "[" && (dialect === "sqlserver" || dialect === "ase"))
    ) {
      const close = c === "[" ? "]" : c;
      const kind = c === "'" ? "string" : "identifier";
      i++;
      let value = "",
        closed = false;
      while (i < sql.length) {
        if (sql[i] === close) {
          if (sql[i + 1] === close) {
            value += close;
            i += 2;
          } else {
            i++;
            closed = true;
            break;
          }
        } else if (
          sql[i] === "\\" &&
          (dialect === "mysql" ||
            (dialect === "postgresql" &&
              kind === "string" &&
              tokens.at(-1)?.end === start &&
              /^e$/i.test(tokens.at(-1)?.value ?? ""))) &&
          i + 1 < sql.length
        ) {
          value += sql[i] + sql[i + 1];
          i += 2;
        } else {
          if (sql[i] === "\n") line++;
          value += sql[i++];
        }
      }
      if (!closed)
        issue("DDL_UNTERMINATED_LITERAL", `Unterminated ${kind}.`, at);
      push(kind, start, at, value, kind === "identifier");
      continue;
    }
    if (/[\p{L}_@$#]/u.test(c)) {
      i++;
      while (i < sql.length && /[\p{L}\p{N}_@$#]/u.test(sql[i])) i++;
      push("word", start, at);
      continue;
    }
    if (/[0-9]/.test(c)) {
      i++;
      while (i < sql.length && /[0-9.eE]/.test(sql[i])) i++;
      push("number", start, at);
      continue;
    }
    i++;
    push("symbol", start, at);
    if (tokens.length > 1000000) {
      issue("DDL_TOKEN_LIMIT", "Token count exceeds 1,000,000.");
      break;
    }
  }
  return { tokens, diagnostics };
}
export const keyword = (token: Token | undefined, word: string) =>
  !!token && token.kind === "word" && token.value.toUpperCase() === word;
export const raw = (tokens: Token[], source: string) =>
  tokens.length
    ? source.slice(tokens[0].start, tokens[tokens.length - 1].end)
    : "";
export function splitTopLevel(tokens: Token[], separator = ","): Token[][] {
  const result: Token[][] = [];
  let part: Token[] = [],
    depth = 0;
  for (const t of tokens) {
    if (t.text === "(" || t.text === "[") depth++;
    if (t.text === ")" || t.text === "]") depth--;
    if (t.text === separator && depth === 0) {
      result.push(part);
      part = [];
    } else part.push(t);
  }
  if (part.length) result.push(part);
  return result;
}
export function balanced(
  tokens: Token[],
  start: number,
): { inside: Token[]; next: number } | undefined {
  if (tokens[start]?.text !== "(") return;
  let depth = 0;
  for (let i = start; i < tokens.length; i++) {
    if (tokens[i].text === "(") depth++;
    if (tokens[i].text === ")" && --depth === 0)
      return { inside: tokens.slice(start + 1, i), next: i + 1 };
    if (tokens[i].text !== ")") continue;
  }
  return;
}
export function splitStatements(
  sql: string,
  dialect: Dialect,
): { statements: Statement[]; diagnostics: Diagnostic[] } {
  const lex = tokenize(sql, dialect),
    statements: Statement[] = [];
  let current: Token[] = [],
    depth = 0,
    delimiter = ";",
    routine = false,
    blockDepth = 0,
    sawBegin = false;
  const flush = (end?: number) => {
    if (current.length) {
      const first = current[0],
        last = current[current.length - 1];
      statements.push({
        tokens: current,
        text: sql.slice(first.start, end ?? last.end).trim(),
        line: first.line,
        start: first.start,
        end: end ?? last.end,
      });
    }
    current = [];
    depth = 0;
    routine = false;
    blockDepth = 0;
    sawBegin = false;
  };
  for (let n = 0; n < lex.tokens.length; n++) {
    const t = lex.tokens[n];
    if (t.kind === "directive" && !/^delimiter\b/i.test(t.text)) {
      flush();
      current = [t];
      flush();
      continue;
    }
    if (t.kind === "directive") {
      if (current.length) flush();
      delimiter = t.text.replace(/^delimiter\s+/i, "").trim();
      if (!delimiter || delimiter.length > 16) {
        lex.diagnostics.push({
          code: "DDL_BAD_DELIMITER",
          severity: "error",
          line: t.line,
          message: "Delimiter must contain 1 to 16 characters.",
        });
        delimiter = ";";
      }
      continue;
    }
    const needsLine =
      ((dialect === "sqlserver" || dialect === "ase") && keyword(t, "GO")) ||
      (dialect === "oracle" && t.text === "/");
    const lineText = needsLine
      ? sql.slice(
          sql.lastIndexOf("\n", t.start - 1) + 1,
          sql.indexOf("\n", t.end) < 0 ? sql.length : sql.indexOf("\n", t.end),
        )
      : "";
    if (
      (dialect === "sqlserver" || dialect === "ase") &&
      keyword(t, "GO") &&
      /^\s*GO(?:\s+\d+)?\s*(?:--.*)?$/i.test(lineText)
    ) {
      flush();
      if (
        lex.tokens[n + 1]?.kind === "number" &&
        lex.tokens[n + 1].line === t.line
      ) {
        lex.diagnostics.push({
          code: "DDL_BATCH_REPEAT",
          severity: "warning",
          line: t.line,
          message:
            "GO repeat count retained as a directive; schema objects are not duplicated.",
        });
        n++;
      }
      continue;
    }
    if (dialect === "oracle" && t.text === "/" && /^\s*\/\s*$/.test(lineText)) {
      flush();
      continue;
    }
    if (t.text === "(") depth++;
    if (t.text === ")") depth--;
    if (depth < 0 || depth > 256) {
      lex.diagnostics.push({
        code: "DDL_UNBALANCED_PARENS",
        severity: "error",
        line: t.line,
        message: "Unbalanced parentheses or nesting over 256.",
      });
      flush();
      continue;
    }
    current.push(t);
    if (dialect === "oracle" && current.length === 1 && (keyword(t, "BEGIN") || keyword(t, "DECLARE"))) routine = true;
    if (
      current.length <= 6 &&
      ["PROCEDURE", "FUNCTION", "TRIGGER", "PACKAGE"].some((k) =>
        keyword(t, k),
      ) &&
      keyword(current[0], "CREATE")
    )
      routine = true;
    if (routine && keyword(t, "BEGIN")) {
      blockDepth++;
      sawBegin = true;
    }
    if (routine && keyword(t, "END")) blockDepth = Math.max(0, blockDepth - 1);
    if (
      delimiter !== ";" &&
      t.kind !== "string" &&
      t.kind !== "identifier" &&
      sql.slice(t.end - delimiter.length, t.end) === delimiter
    ) {
      const end = t.end - delimiter.length;
      while (current.length && current[current.length - 1].start >= end)
        current.pop();
      flush(end);
      continue;
    }
    if (
      t.text === ";" &&
      depth === 0 &&
      (!routine ||
        dialect === "postgresql" ||
        (dialect === "mysql" && sawBegin && blockDepth === 0))
    ) {
      current.pop();
      flush(t.start);
    }
  }
  if (depth !== 0)
    lex.diagnostics.push({
      code: "DDL_UNBALANCED_PARENS",
      severity: "error",
      line: current[0]?.line,
      message: "Unclosed parentheses.",
    });
  flush();
  return { statements, diagnostics: lex.diagnostics };
}
